import React, { useCallback, useEffect } from "react";
import { Button, Modal, Result, Spin } from "antd";

import Icons from "components/layout/icons";
import Localize from "components/service/localize";
import {
    ATTENDANCE_ACTIONS,
    ATTENDANCE_SCAN_STATES,
    useAttendanceScan
} from "components/attendance/hooks";


const RESULT_TEXT_BY_REASON = {
    outside_radius: "ATTENDANCE.Result_OutsideRadius_Text",
    invalid_qr: "ATTENDANCE.Result_InvalidQr_Text",
    invalid_qr_code: "ATTENDANCE.Result_InvalidQr_Text",
    qr_invalid: "ATTENDANCE.Result_InvalidQr_Text",
    duplicate_scan: "ATTENDANCE.Result_Duplicate_Text",
    permission_denied: "ATTENDANCE.Error_GpsPermission_Text",
    gps_unavailable: "ATTENDANCE.Error_GpsUnavailable_Text"
};

const getActionTitleKey = (action) => (
    action === ATTENDANCE_ACTIONS.CHECK_OUT ?
        "ATTENDANCE.Modal_Title_CheckOut" :
        "ATTENDANCE.Modal_Title_CheckIn"
);

const getSuccessTitleKey = (action) => (
    action === ATTENDANCE_ACTIONS.CHECK_OUT ?
        "ATTENDANCE.Result_CheckOutAccepted_Title" :
        "ATTENDANCE.Result_CheckInAccepted_Title"
);

const getResultTone = (result) => {
    if (result?.result === "accepted") {
        return "success";
    }

    if (result?.result === "ignored") {
        return "warning";
    }

    return "error";
};

const getResultTitleKey = (result, action) => {
    if (result?.result === "accepted") {
        return getSuccessTitleKey(action);
    }

    if (result?.result === "ignored") {
        return "ATTENDANCE.Result_Duplicate_Title";
    }

    return "ATTENDANCE.Result_Rejected_Title";
};

const getResultIcon = (tone) => {
    if (tone === "success") {
        return <Icons.Check type="circle" />;
    }

    if (tone === "warning") {
        return <Icons.Warning />;
    }

    return <Icons.Cross />;
};

const ResultSubtitle = ({ result }) => {
    if (result?.result === "accepted") {
        return (
            <Localize serverTime={ result?.server_time || "-" }>
                ATTENDANCE.Result_Accepted_Text
            </Localize>
        );
    }

    const reasonTextKey = RESULT_TEXT_BY_REASON[result?.reason];

    if (reasonTextKey) {
        return <Localize>{ reasonTextKey }</Localize>;
    }

    return result?.detail || <Localize>ATTENDANCE.Result_GenericRejected_Text</Localize>;
};

const AttendanceScannerModal = ({
        visible = false,
        action = ATTENDANCE_ACTIONS.CHECK_IN,
        eeId,
        onClose = () => {}
    }) => {
    const {
        readerId,
        scanState,
        result,
        failure,
        startScanner,
        stopScanner,
        retry,
        reset
    } = useAttendanceScan({
        action,
        eeId
    });

    const closeScanner = useCallback(() => {
        stopScanner();
        reset();
        onClose();
    }, [
        onClose,
        reset,
        stopScanner
    ]);

    useEffect(() => {
        if (!visible) {
            stopScanner();
            reset();

            return undefined;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            startScanner();
        });

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            stopScanner();
        };
    }, [
        action,
        eeId,
        reset,
        startScanner,
        stopScanner,
        visible
    ]);

    const renderScanner = () => (
        <div className="attendance-scanner">
            <div className="attendance-scanner-camera">
                <div id={ readerId } className="attendance-scanner-reader" />
                <div className="attendance-scanner-frame">
                    <span className="corner top-left" />
                    <span className="corner top-right" />
                    <span className="corner bottom-left" />
                    <span className="corner bottom-right" />
                </div>
            </div>
            <div className="attendance-scanner-hint">
                <Icons.Search />
                <Localize>ATTENDANCE.Scanner_Hint</Localize>
            </div>
            <div className="attendance-scanner-actions">
                <Button type="default" onClick={ closeScanner }>
                    <Localize>GLOBAL.Button_Text_Cancel</Localize>
                </Button>
            </div>
        </div>
    );

    const renderProgress = () => (
        <div className="attendance-processing">
            <Spin size="large" />
            <h3>
                <Localize>
                    { scanState === ATTENDANCE_SCAN_STATES.LOCATING ?
                        "ATTENDANCE.Processing_Locating_Title" :
                        "ATTENDANCE.Processing_Submitting_Title" }
                </Localize>
            </h3>
            <p className="text-type3-white-op60">
                <Localize>
                    { scanState === ATTENDANCE_SCAN_STATES.LOCATING ?
                        "ATTENDANCE.Processing_Locating_Text" :
                        "ATTENDANCE.Processing_Submitting_Text" }
                </Localize>
            </p>
        </div>
    );

    const renderFailure = () => (
        <Result
            className="attendance-result attendance-result-error"
            status="error"
            icon={ <Icons.Warning /> }
            title={ <Localize>{ failure?.title || "ATTENDANCE.Error_Generic_Title" }</Localize> }
            subTitle={ <Localize>{ failure?.text || "ATTENDANCE.Error_Generic_Text" }</Localize> }
            extra={[
                failure?.reason !== "insecure_context" && (
                    <Button key="retry" type="primary" onClick={ retry }>
                        <Localize>ATTENDANCE.Button_RetryScan</Localize>
                    </Button>
                ),
                <Button key="close" type="default" onClick={ closeScanner }>
                    <Localize>ATTENDANCE.Button_Close</Localize>
                </Button>
            ].filter(Boolean)}
        />
    );

    const renderResult = () => {
        const tone = getResultTone(result);

        return (
            <Result
                className={ `attendance-result attendance-result-${ tone }` }
                status={ tone }
                icon={ getResultIcon(tone) }
                title={ <Localize>{ getResultTitleKey(result, action) }</Localize> }
                subTitle={ <ResultSubtitle result={ result } /> }
                extra={ result?.result === "accepted" ? [
                    <Button key="close" type="primary" onClick={ closeScanner }>
                        <Localize>ATTENDANCE.Button_Close</Localize>
                    </Button>
                ] : [
                    <Button key="retry" type="primary" onClick={ retry }>
                        <Localize>ATTENDANCE.Button_RetryScan</Localize>
                    </Button>,
                    <Button key="close" type="default" onClick={ closeScanner }>
                        <Localize>ATTENDANCE.Button_Close</Localize>
                    </Button>
                ] }
            />
        );
    };

    const renderContent = () => {
        if (
            scanState === ATTENDANCE_SCAN_STATES.LOCATING ||
            scanState === ATTENDANCE_SCAN_STATES.SUBMITTING
        ) {
            return renderProgress();
        }

        if (scanState === ATTENDANCE_SCAN_STATES.RESULT) {
            return renderResult();
        }

        if (scanState === ATTENDANCE_SCAN_STATES.FAILED) {
            return renderFailure();
        }

        return renderScanner();
    };

    return (
        <Modal
            className="modal-standard attendance-scanner-modal full dark"
            visible={ visible }
            title={ <Localize>{ getActionTitleKey(action) }</Localize> }
            closeIcon={ <Icons.Cross /> }
            footer={ null }
            destroyOnClose
            centered={ false }
            onCancel={ closeScanner }
        >
            { renderContent() }
        </Modal>
    );
};

export default AttendanceScannerModal;
